import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
    DeliveryNotesService,
    PdfService,
    FineDiscrepanciesService,
    CheckinsService,
    ToursService,
    DeliveryNoteBundlesService,
} from 'api/services';
import { AppStore } from '@app/stores/appStore';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { CurrentSiteRole } from '@app/shared/models/authentication/session';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';

import { NgxSpinnerService } from 'ngx-spinner';
import { ErrorHandler } from '@app/core/services/error-handler.service';
import { ViewBundleDialogComponent } from '@app/shared/components/view-bundle-dialog/view-bundle-dialog.component';
import {
    DeliveryNoteBundleOverview,
    GetConsigneeTour,
    Event as ApiEvent,
    ConsigneeDeliveryNoteBundle,
    ConsigneeLookupDeliveryNoteResponse,
    DeliveryNoteKeyAndCheckinKeyLookup,
    CheckinKeyAndDeliveryNoteKeyLookup,
    EventPayload,
    CheckinFinishedEventPayload,
    UserRoles,
} from 'api/models';
import { ShowPdfDialogComponent } from '@app/shared/components/show-pdf-dialog/show-pdf-dialog.component';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { SearchStore } from '@app/stores/searchStore';
import { Router } from '@angular/router';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { ApiInterceptor } from '@app/core/interceptors/api.interceptor';
import {
    DeliveryNoteData,
    FolderData,
    TourData,
    downloadBlobAsFile,
} from '@app/shared/models/table-data-types';

// Type guard to check if payload has checkinKey
function hasCheckinKey(
    payload: EventPayload
): payload is CheckinFinishedEventPayload {
    return (
        payload &&
        typeof payload === 'object' &&
        'checkinKey' in payload &&
        typeof (payload as CheckinFinishedEventPayload).checkinKey === 'string'
    );
}

@Injectable({
    providedIn: 'root',
})
export class ConsigneeViewActionsService {
    private dialog = inject(MatDialog);
    private spinner = inject(NgxSpinnerService);
    private errorHandler = inject(ErrorHandler);
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);

    constructor(
        private deliveryNotesService: DeliveryNotesService,
        private pdfService: PdfService,
        private fineDiscrepanciesService: FineDiscrepanciesService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private checkinsService: CheckinsService,
        private toursService: ToursService,
        private router: Router,
        private apiInterceptor: ApiInterceptor,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private sessionService: SessionService,
        private storageService: StorageService
    ) {}

    /**
     * Return the url of the delivery note pdf for downloading instantly
     * @param bundle: DeliveryNoteBundleOverview
     */
    async downloadDeliveryBundle(bundle: FolderData): Promise<void> {
        this.spinner.show();
        try {
            const response = await lastValueFrom(
                this.deliveryNotesService.getConsigneeDeliveryNotePdfsForBundle(
                    {
                        organizationSiteKey: bundle.accessSiteKey,
                        deliveryNoteBundleKey: bundle._key,
                    }
                )
            );

            const url = window.URL.createObjectURL(response);
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.href = url;
            const bundleNumber = bundle.bundleNumber;
            a.download = bundleNumber.replace('.', '-') + '.pdf';
            a.click();
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    viewFolderAsPdf(bundle: FolderData): void {
        this.dialog.open(ViewBundleDialogComponent, {
            data: {
                deliveryNoteBundle: bundle,
                organizationSiteKey: bundle.accessSiteKey,
                context: 'consignee',
            },
            disableClose: true,
            width: '800px',
        });
    }

    /**
     * Downloads a single delivery note PDF
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle: FolderData (optional) - The parent bundle containing the delivery note
     */
    async downloadDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData
    ): Promise<void> {
        this.spinner.show();
        try {
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                this.appStore.currentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            const response = await lastValueFrom(
                this.pdfService.getConsigneeDeliveryNoteCombinedPdf$Response({
                    organizationSiteKey: organizationSiteKey,
                    deliveryNoteKey: deliveryNote._key,
                })
            );

            const filename =
                response.headers.get('content-disposition')?.split('=')[1] ||
                deliveryNote.fileName;
            downloadBlobAsFile(response.body, filename);
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Views a single delivery note in a dialog
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle: FolderData (optional) - The parent bundle containing the delivery note
     */
    async viewDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData
    ): Promise<void> {
        this.spinner.show();
        try {
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                this.appStore.currentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            const blobData = await lastValueFrom(
                this.pdfService.getConsigneeDeliveryNoteCombinedPdf({
                    organizationSiteKey: organizationSiteKey,
                    deliveryNoteKey: deliveryNote._key,
                })
            );

            this.dialog.open(ShowPdfDialogComponent, {
                minWidth: '80%',
                height: '95%',
                disableClose: true,
                data: {
                    data: blobData,
                },
            });
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Opens a file picker to add fine discrepancy for a delivery note
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle?: FolderData | TourData - Optional parent bundle/tour for context
     */
    addFineDiscrepancy(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData | TourData
    ): void {
        // Create a file input element
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.multiple = true;
        fileInput.accept = '.pdf,.jpg,.jpeg,.png,.heic';

        fileInput.onchange = async (event: Event) => {
            const files = (event.target as HTMLInputElement).files;
            if (files && files.length > 0) {
                this.spinner.show();
                try {
                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const filename: string = file.name;
                        // Use parent bundle's accessSiteKey first, then delivery note's accessSiteKey, then fallback to current org key
                        const organizationSiteKey =
                            (parentBundle && 'accessSiteKey' in parentBundle
                                ? parentBundle.accessSiteKey
                                : undefined) ||
                            deliveryNote.accessSiteKey ||
                            this.appStore.currentOrganizationSiteKey();

                        if (!organizationSiteKey) {
                            this.toastService.error(
                                this.translateService.instant('GENERAL-ERROR')
                            );
                            return;
                        }

                        await lastValueFrom(
                            this.fineDiscrepanciesService.postNewFineDiscrepancy(
                                {
                                    organizationSiteKey: organizationSiteKey,
                                    deliveryNoteKey: deliveryNote._key,
                                    fileName: filename,
                                    body: file,
                                }
                            )
                        );
                    }

                    this.toastService.success(
                        this.translateService.instant(
                            'INCOMING-GOODS.CHECKIN.FINE-DISCREPANCY-ADDED'
                        )
                    );

                    // Trigger data refresh to reload the table
                    this.searchStore.triggerDataRefresh();
                } catch (error: any) {
                    if (error?.status === 409) {
                        // Conflict - fine discrepancy already exists
                        this.toastService.warning(
                            this.translateService.instant(
                                'INCOMING-GOODS.CHECKIN.FINE-DISCREPANCY-EXISTS'
                            )
                        );
                    } else {
                        this.toastService.error(
                            this.translateService.instant(
                                'INCOMING-GOODS.CHECKIN.FINE-DISCREPANCY-ADDED-ERROR'
                            )
                        );
                        this.errorHandler.handleError(error);
                    }
                } finally {
                    this.spinner.hide();
                }
            }
        };

        // Trigger the file picker
        fileInput.click();
    }

    /**
     * Check in delivery notes from selected tours
     * @param selectedTours: TourData[] - Array of selected tour objects
     */
    async checkinTourDeliveryNotes(selectedTours: TourData[]): Promise<void> {
        if (!selectedTours || selectedTours.length === 0) {
            return;
        }

        const siteKey: string = selectedTours[0].orgSiteKey;

        try {
            const keys =
                await this.getDeliveryNoteKeysFromSelectedTours(selectedTours);

            this.apiInterceptor.disableToastService();
            const response = await lastValueFrom(
                this.checkinsService.getConsigneeCheckinLookup({
                    organizationSiteKey: siteKey,
                    body: keys,
                })
            );
            this.apiInterceptor.enableToastService();

            // Check if there are no valid checkin keys
            const hasValidCheckinKey = response.deliveryNotes.some(
                (note: DeliveryNoteKeyAndCheckinKeyLookup) =>
                    note.checkinKey && note.checkinKey.trim() !== ''
            );

            if (!hasValidCheckinKey && response.checkins.length === 0) {
                throw new Error('No valid checkin keys found');
            }

            // Extract checkin keys from both deliveryNotes and checkins
            const deliveryNoteCheckinKeys = response.deliveryNotes
                .map(
                    (note: DeliveryNoteKeyAndCheckinKeyLookup) =>
                        note.checkinKey
                )
                .filter((key: string) => key && key.trim() !== '');
            const checkinCheckinKeys = response.checkins
                .map(
                    (checkin: CheckinKeyAndDeliveryNoteKeyLookup) =>
                        checkin.checkinKey
                )
                .filter((key: string) => key && key.trim() !== '');

            // Combine both arrays to check for consistency
            const allCheckinKeys = [
                ...deliveryNoteCheckinKeys,
                ...checkinCheckinKeys,
            ];

            const allCheckinKeysSame = allCheckinKeys.every(
                key => key === allCheckinKeys[0]
            );

            // Check if deliveryNoteKeysExact is false in any of the checkins
            const deliveryNoteKeysExact = response.checkins.every(
                (checkin: CheckinKeyAndDeliveryNoteKeyLookup) =>
                    checkin.deliveryNoteKeysExact
            );

            if (allCheckinKeysSame && deliveryNoteKeysExact) {
                this.router.navigate(
                    [
                        RoutingEndpoints.consignee,
                        RoutingEndpoints.consignee_checkin_notes,
                    ],
                    {
                        queryParams: {
                            organizationSiteKey: siteKey,
                            checkinKey: allCheckinKeys[0],
                            tourKey: selectedTours[0]._key,
                        },
                    }
                );
            } else {
                throw new Error();
            }
        } catch (error) {
            this.apiInterceptor.enableToastService();
            try {
                const keys =
                    await this.getDeliveryNoteKeysFromSelectedTours(
                        selectedTours
                    );
                const response = await lastValueFrom(
                    this.checkinsService.postConsigneeCheckinDeliveryNotes({
                        organizationSiteKey: siteKey,
                        body: keys,
                    })
                );

                this.router.navigate(
                    [
                        RoutingEndpoints.consignee,
                        RoutingEndpoints.consignee_checkin_notes,
                    ],
                    {
                        queryParams: {
                            organizationSiteKey: siteKey,
                            checkinKey: response._key,
                            tourKey: selectedTours[0]._key,
                        },
                    }
                );
            } catch (e: any) {
                if (e.status === 409) {
                    this.toastService.error(e.error.errorMessage);
                } else {
                    this.toastService.error(
                        this.translateService.instant(
                            'INCOMING-GOODS.CHECKIN.ERROR'
                        )
                    );
                }
            }
        }
    }

    private async getDeliveryNoteKeysFromSelectedTours(
        selectedTours: TourData[]
    ): Promise<string[]> {
        const promises: Array<Promise<GetConsigneeTour>> = [];

        selectedTours.forEach(tour => {
            promises.push(
                lastValueFrom(
                    this.toursService.getConsigneeTour({
                        organizationSiteKey: tour.orgSiteKey,
                        tourKey: tour._key,
                    })
                )
            );
        });

        const tours = await Promise.all(promises);

        const keys: Array<string> = [];

        tours.forEach(tour => {
            tour.deliveryNotes.forEach(note => {
                keys.push(note._key);
            });
        });

        return keys;
    }

    /**
     * Navigates to the check-in page for a notification event
     * @param event The notification event row data
     */
    async openCheckInPage(event: ApiEvent): Promise<void> {
        // Check if the event is a CheckinEvent
        if (hasCheckinKey(event.payload)) {
            try {
                this.apiInterceptor.disableToastService();
                const response = await lastValueFrom(
                    this.checkinsService.getConsigneeCheckin({
                        organizationSiteKey: event.organizationSiteKey,
                        checkinKey: event.payload.checkinKey,
                    })
                );
                this.apiInterceptor.enableToastService();
                if (response && response.status === 'finished') {
                    this.toastService.info(
                        this.translateService.instant(
                            'SELFSERVICE.PROCESS_FINISHED'
                        )
                    );
                } else {
                    // Switch to the notification's location before navigating
                    this.switchToEventLocation(event.organizationSiteKey);

                    const organizationSiteKey = event.organizationSiteKey;
                    const checkinKey = event.payload.checkinKey;
                    const url = `/consignee/checkin-notes?organizationSiteKey=${organizationSiteKey}&checkinKey=${checkinKey}`;
                    this.router.navigateByUrl(url);
                }
            } catch (error) {
                this.toastService.info(
                    this.translateService.instant(
                        'SELFSERVICE.PROCESS_NOT_ACCESSIBLE'
                    )
                );
            }
        }
        this.apiInterceptor.enableToastService();
    }

    /**
     * Switches to the specified organization site if it differs from the current site.
     * Uses individual store setters to avoid triggering data refresh.
     * @param targetOrgSiteKey The organization site key to switch to
     */
    private switchToEventLocation(targetOrgSiteKey: string): void {
        const currentOrgSiteKey = this.appStore.currentOrganizationSiteKey();

        // Only switch if the event is from a different location
        if (targetOrgSiteKey && targetOrgSiteKey !== currentOrgSiteKey) {
            const session = this.sessionService.getSession();
            const targetSite = session.organizationSites.find(
                site => site._key === targetOrgSiteKey
            );

            if (targetSite) {
                // Use same role determination logic as user-profile.component.ts
                let role = 'consignee';
                if (targetSite.roles.includes(UserRoles.RemoteConsignee)) {
                    role = 'remote_consignee';
                }

                const siteRole: CurrentSiteRole = {
                    role: role,
                    siteInfo: targetSite,
                };

                // Use INDIVIDUAL setters (no data refresh) to avoid table reload
                this.appStore.setCurrentOrganizationSite(targetSite);
                this.appStore.setCurrentSiteRole(siteRole);

                // Update session service observables (triggers header update)
                this.sessionService.currentSiteRole.set(siteRole);
                this.sessionService.setCurrentOrganizationSite(targetSite._key);

                // Persist to localStorage
                this.storageService.setItem('currentSiteRole', siteRole);
            }
        }
    }

    /**
     * Check in delivery note bundles from selected folders
     * @param selectedBundles: FolderData[] - Array of selected bundle objects
     */
    async checkinDeliveryNoteBundles(
        selectedBundles: FolderData[]
    ): Promise<void> {
        if (!selectedBundles || selectedBundles.length === 0) {
            return;
        }

        const siteKey: string = selectedBundles[0].accessSiteKey;
        const keys =
            await this.getDeliveryNoteKeysFromSelectedBundles(selectedBundles);

        try {
            this.apiInterceptor.disableToastService();
            const response = await lastValueFrom(
                this.checkinsService.getConsigneeCheckinLookup({
                    organizationSiteKey: siteKey,
                    body: keys,
                })
            );
            this.apiInterceptor.enableToastService();

            // Check if there are no valid checkin keys
            const hasValidCheckinKey = response.deliveryNotes.some(
                (note: DeliveryNoteKeyAndCheckinKeyLookup) =>
                    note.checkinKey && note.checkinKey.trim() !== ''
            );

            if (!hasValidCheckinKey && response.checkins.length === 0) {
                throw new Error('No valid checkin keys found');
            }

            // Extract checkin keys from both deliveryNotes and checkins
            const deliveryNoteCheckinKeys = response.deliveryNotes
                .map(
                    (note: DeliveryNoteKeyAndCheckinKeyLookup) =>
                        note.checkinKey
                )
                .filter((key: string) => key && key.trim() !== '');
            const checkinCheckinKeys = response.checkins
                .map(
                    (checkin: CheckinKeyAndDeliveryNoteKeyLookup) =>
                        checkin.checkinKey
                )
                .filter((key: string) => key && key.trim() !== '');

            // Combine both arrays to check for consistency
            const allCheckinKeys = [
                ...deliveryNoteCheckinKeys,
                ...checkinCheckinKeys,
            ];

            const allCheckinKeysSame = allCheckinKeys.every(
                key => key === allCheckinKeys[0]
            );

            // Check if deliveryNoteKeysExact is false in any of the checkins
            const deliveryNoteKeysExact = response.checkins.every(
                (checkin: CheckinKeyAndDeliveryNoteKeyLookup) =>
                    checkin.deliveryNoteKeysExact
            );

            if (allCheckinKeysSame && deliveryNoteKeysExact) {
                this.router.navigate(
                    [
                        RoutingEndpoints.consignee,
                        RoutingEndpoints.consignee_checkin_notes,
                    ],
                    {
                        queryParams: {
                            organizationSiteKey: siteKey,
                            checkinKey: allCheckinKeys[0],
                            checkinBundleKeys:
                                this.getCheckinBundleKeys(selectedBundles),
                        },
                    }
                );
            } else {
                throw new Error(
                    'Inconsistent checkin keys or deliveryNoteKeysExact is false'
                );
            }
        } catch (error) {
            this.apiInterceptor.enableToastService();
            try {
                const response = await lastValueFrom(
                    this.checkinsService.postConsigneeCheckinDeliveryNotes({
                        organizationSiteKey: siteKey,
                        body: keys,
                    })
                );

                this.router.navigate(
                    [
                        RoutingEndpoints.consignee,
                        RoutingEndpoints.consignee_checkin_notes,
                    ],
                    {
                        queryParams: {
                            organizationSiteKey: siteKey,
                            checkinKey: response._key,
                            checkinBundleKeys:
                                this.getCheckinBundleKeys(selectedBundles),
                        },
                    }
                );
            } catch (e: any) {
                if (e.status === 409) {
                    this.toastService.error(e.error.errorMessage);
                } else {
                    this.toastService.error(
                        this.translateService.instant(
                            'INCOMING-GOODS.CHECKIN.ERROR'
                        )
                    );
                }
            }
        }
    }

    private async getDeliveryNoteKeysFromSelectedBundles(
        selectedBundles: FolderData[]
    ): Promise<string[]> {
        const promises: Array<Promise<ConsigneeDeliveryNoteBundle>> = [];

        selectedBundles.map(bundle => {
            promises.push(
                lastValueFrom(
                    this.deliveryNoteBundlesService.getConsigneeBundle({
                        organizationSiteKey: bundle.accessSiteKey,
                        deliveryNoteBundleKey: bundle._key,
                    })
                )
            );
        });

        const bundles = await Promise.all(promises);

        const keys: Array<string> = [];

        bundles.forEach(bundle => {
            bundle.deliveryNotes.forEach(note => {
                if (!note.dateReceived) {
                    keys.push(note._key);
                }
            });
        });

        return keys;
    }

    private getCheckinBundleKeys(bundles: FolderData[]): string[] {
        const checkinBundleKeys: string[] = [];
        bundles.map(bundle => {
            checkinBundleKeys.push(bundle._key);
        });
        return checkinBundleKeys;
    }

    /**
     * Opens the scan wallet page for QR code scanning
     */
    scanWallet(): void {
        this.router.navigate(
            [
                RoutingEndpoints.consignee,
                RoutingEndpoints.consignee_checkin_notes,
            ],
            {
                queryParams: {
                    scan: true,
                    organizationSiteKey:
                        this.appStore.currentOrganizationSiteKey(),
                },
            }
        );
    }
}
